import {
    IPlayerState,
    IChestUnlock,
    IPlayerUnlock,
    PlayerUnlock,
    UnlockType,
    IPostcardUnlock,
} from '../../../State/Player/IPlayerState';
import IPlayerData from '../../IPlayerData';
import { restoreStringifiedArray } from '../../ParseHelper';
import PlayerProperty from '../PlayerProperty';

export default class Unlocks extends PlayerProperty<
    IPlayerUnlock[],
    {
        all_chests: string;
        unlocked_postcards: string;
        suitcase_add: number;
    }
> {
    public readonly defaultValue: IPlayerUnlock[] = [];

    public restore(playerEndpointData: IPlayerData): IPlayerUnlock[] {
        const unlocks: PlayerUnlock[] = [];

        switch (playerEndpointData.suitcase_add) {
            case 0:
                break;
            case 1:
                unlocks.push(
                    { type: UnlockType.SUITCASE },
                    { type: UnlockType.BOOSTER_TNT }
                );
                break;
            case 2:
                unlocks.push(
                    { type: UnlockType.SUITCASE },
                    { type: UnlockType.BOOSTER_TNT },
                    { type: UnlockType.BOOSTER_BULLS_EYE }
                );
                break;
            case 3:
                unlocks.push(
                    { type: UnlockType.SUITCASE },
                    { type: UnlockType.BOOSTER_TNT },
                    { type: UnlockType.BOOSTER_BULLS_EYE },
                    { type: UnlockType.BOOSTER_SHUFFLE }
                );
                break;
        }

        const chestData = restoreStringifiedArray<number>(
            playerEndpointData.all_chests,
            []
        );

        chestData.forEach((value, index) => {
            // Check chest is opened.
            if (value === 1) {
                unlocks.push({
                    type: UnlockType.CHEST,
                    id: `chest_${index + 1}`,
                });
            }
        });

        restoreStringifiedArray<string>(
            playerEndpointData.unlocked_postcards,
            []
        ).forEach(value =>
            unlocks.push({ type: UnlockType.POSTCARD, id: value })
        );

        return unlocks;
    }

    public save(playerStateData: IPlayerState) {
        // Convert suitcase and booster unlocks to legacy format.
        let suitcase_add = 0;

        // prettier-ignore
        if (playerStateData.playerUnlocks.find(unlock => unlock.type === UnlockType.SUITCASE)) {
            suitcase_add = 1;
        }
        // prettier-ignore
        if (playerStateData.playerUnlocks.find(unlock => unlock.type === UnlockType.BOOSTER_BULLS_EYE)) {
            suitcase_add = 2;
        }
        // prettier-ignore
        if (playerStateData.playerUnlocks.find(unlock => unlock.type === UnlockType.BOOSTER_SHUFFLE)) {
            suitcase_add = 3;
        }

        // Convert chest unlocks to legacy format.
        const chestUnlocks: IChestUnlock[] = playerStateData.playerUnlocks.filter(
            (
                unlock: PlayerUnlock
            ): unlock is IChestUnlock /* Cannot infer type? */ =>
                unlock.type === UnlockType.CHEST
        );

        // Find highest index of unlocked chests in legacy format.
        const maxIndex = chestUnlocks.reduce((previousValue, currentValue) => {
            return Math.max(this.getChestIndex(currentValue.id), previousValue);
        }, 0);

        // Pre-fill all_chests array with zeroes up until highest index.
        const allChests = new Array(maxIndex).fill(0);

        chestUnlocks.forEach(chest => {
            // Set chest index to '1' to indicate an opened chest.
            // prettier-ignore
            allChests[this.getChestIndex(chest.id)] = 1;
        });

        const postcards: IPostcardUnlock[] = playerStateData.playerUnlocks.filter(
            (unlock: PlayerUnlock): unlock is IPostcardUnlock =>
                unlock.type === UnlockType.POSTCARD
        );

        return {
            all_chests: JSON.stringify(allChests),
            unlocked_postcards: JSON.stringify(postcards.map(x => x.id)),
            suitcase_add: suitcase_add,
        };
    }

    public isChanged(
        playerStateData: IPlayerState,
        playerEndpointData: IPlayerData
    ): boolean {
        const saveData = this.save(playerStateData);

        return (
            saveData.suitcase_add !== playerEndpointData.suitcase_add ||
            saveData.all_chests !== playerEndpointData.all_chests ||
            saveData.unlocked_postcards !==
                playerEndpointData.unlocked_postcards
        );
    }

    private getChestIndex(chestID: string): number {
        return parseInt(chestID.split('_')[1]) - 1;
    }
}
